import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Skeleton, Tooltip } from '@mui/material';
import {
    IoDownloadOutline,
    IoArrowUpCircleOutline,
    IoArrowDownCircleOutline,
    IoCheckmarkCircleOutline,
    IoTimeOutline,
    IoAlertCircleOutline,
    IoCloseCircleOutline,
    IoCloseOutline,
    IoRefreshOutline,
    IoWarningOutline,
} from 'react-icons/io5';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import BillingService from '@Helpers/URLHelpers/BillingService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import './BillingSettingsPage.scss';

const billingService = new BillingService();
const INVOICE_PAGE_SIZE = 10;

const STATUS_META = {
    ACTIVE:    { label: 'Active',    icon: <IoCheckmarkCircleOutline />, color: '#22c55e' },
    TRIALING:  { label: 'Trialing',  icon: <IoTimeOutline />,            color: '#f59e0b' },
    HALTED:    { label: 'Payment Failed', icon: <IoAlertCircleOutline />, color: '#ef4444' },
    CANCELLED: { label: 'Cancelled', icon: <IoCloseCircleOutline />,     color: '#94a3b8' },
};

const FEATURE_LABELS = {
    multi_page_forms: 'Multi-page Forms',
    assignee_matrix: 'Assignee Matrix',
    priority_matrix: 'Priority Matrix',
    escalation_matrix: 'Escalation Matrix',
    label_matrix: 'Label Matrix',
    sla_tracking: 'SLA Tracking',
    advanced_reports: 'Advanced Reports',
    ai_summary: 'AI Summary',
    csat: 'CSAT',
    white_label: 'White Label',
    ai_chatbot: 'AI Chatbot',
};

const paise2Rupees = (paise) => {
    if (paise == null) return '—';
    return '₹' + (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
};

const formatDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const parseServiceBody = (responseBody) => {
    if (!responseBody) return null;
    const parsedBody = JSON.parse(responseBody);
    return parsedBody?.data ?? parsedBody;
};

const isSuccessfulResponse = (response) => {
    if (response?.isSuccess === true) return true;
    if (!response || response.error) return false;
    if (response.body !== undefined && response.body !== null) return true;
    return typeof response.message === 'string' && response.message.trim().toLowerCase() === 'success';
};

export default function BillingSettingsPage() {

    const { currentOrgId } = useReduxOrg();
    const { subscription, setCurrentSubscription } = useReduxSubscription();

    const [activeTab, setActiveTab] = useState('plan');

    const [plans, setPlans] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [usage, setUsage] = useState(null);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [loadingUsage, setLoadingUsage] = useState(true);

    const [invoicePage, setInvoicePage] = useState(0);
    const [invoiceFilter, setInvoiceFilter] = useState('lifetime');
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

    // Upgrade/downgrade UI state
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [proration, setProration] = useState(null);
    const [loadingProration, setLoadingProration] = useState(false);
    const [confirmingUpgrade, setConfirmingUpgrade] = useState(false);
    const [confirmingDowngrade, setConfirmingDowngrade] = useState(false);
    const [showPlanEditor, setShowPlanEditor] = useState(false);

    // Coupon state for upgrade flow
    const [upgradeCouponInput, setUpgradeCouponInput] = useState('');
    const [upgradeCouponApplied, setUpgradeCouponApplied] = useState(null);
    const [upgradeCouponError, setUpgradeCouponError] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);

    // Razorpay
    const razorpayKeyRef = useRef(null);
    const razorpayScriptRef = useRef(null);

    // Cancel UI state
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [confirmingCancel, setConfirmingCancel] = useState(false);

    // Reactivate UI state
    const [reactivating, setReactivating] = useState(false);
    const [restarting, setRestarting] = useState(false);

    // Billing profile
    const [billingProfile, setBillingProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [editingProfile, setEditingProfile] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({
        billingName: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'IN',
        gstNumber: '',
        panNumber: '',
        vatNumber: '',
    });

    const className = 'billingSettingsPage';

    const refreshSubscription = useCallback(async () => {
        if (!currentOrgId) return;
        try {
            const data = await billingService.getSubscriptionByOrgId(currentOrgId);
            if (data?.body) {
                const sub = parseServiceBody(data.body);
                if (sub) setCurrentSubscription(sub);
            }
        } catch (_) {}
    }, [currentOrgId, setCurrentSubscription]);

    useEffect(() => {
        refreshSubscription();
    }, [refreshSubscription]);

    const loadRazorpayScript = useCallback(() => {
        if (razorpayScriptRef.current) return razorpayScriptRef.current;
        razorpayScriptRef.current = new Promise((resolve) => {
            if (window.Razorpay) { resolve(); return; }
            const existing = document.getElementById('razorpay-checkout-js');
            if (existing) { existing.addEventListener('load', resolve); return; }
            const script = document.createElement('script');
            script.id = 'razorpay-checkout-js';
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = resolve;
            script.onerror = resolve;
            document.body.appendChild(script);
        });
        return razorpayScriptRef.current;
    }, []);

    useEffect(() => {
        billingService.fetchBillingConfig()
            .then(data => { if (data?.razorpayKeyId) razorpayKeyRef.current = data.razorpayKeyId; })
            .catch(() => {});
        loadRazorpayScript();
    }, [loadRazorpayScript]);

    useEffect(() => {
        billingService.getAllPlans()
            .then(data => {
                if (data?.body) {
                    try { setPlans(parseServiceBody(data.body) || []); } catch (_) {}
                }
            })
            .finally(() => setLoadingPlans(false));

        if (currentOrgId) {
            billingService.getInvoicesByOrgId(currentOrgId)
                .then(data => {
                    if (data?.body) {
                        try { setInvoices(parseServiceBody(data.body) || []); } catch (_) {}
                    }
                })
                .finally(() => setLoadingInvoices(false));

            billingService.getCurrentUsage(currentOrgId)
                .then(data => {
                    if (data?.body) {
                        try { setUsage(parseServiceBody(data.body)); } catch (_) {}
                    }
                })
                .finally(() => setLoadingUsage(false));

            setLoadingProfile(true);
            billingService.getBillingProfile(currentOrgId)
                .then(data => {
                    if (data?.body) {
                        try {
                            const profile = parseServiceBody(data.body);
                            if (profile) {
                                setBillingProfile(profile);
                                setProfileForm({
                                    billingName: profile.legalName || '',
                                    email: profile.billingEmail || '',
                                    addressLine1: profile.addressLine1 || '',
                                    addressLine2: profile.addressLine2 || '',
                                    city: profile.city || '',
                                    state: profile.state || '',
                                    postalCode: profile.postalCode || '',
                                    country: profile.countryCode || 'IN',
                                    gstNumber: profile.gstNumber || '',
                                    panNumber: profile.panNumber || '',
                                    vatNumber: profile.vatNumber || '',
                                });
                            }
                        } catch (_) {}
                    }
                })
                .finally(() => setLoadingProfile(false));
        } else {
            setLoadingInvoices(false);
            setLoadingUsage(false);
        }
    }, [currentOrgId]);

    const currentPlan = subscription?.plan;
    const statusMeta = STATUS_META[subscription?.status] || { label: subscription?.status, icon: null, color: '#94a3b8' };
    const activePlans = useMemo(() => plans.filter(p => p.isActive).sort((a, b) => a.id - b.id), [plans]);
    const comparisonFeatureKeys = useMemo(() => {
        const featureSet = new Set();
        activePlans.forEach(plan => {
            Object.keys(plan?.features || {}).forEach(key => featureSet.add(key));
        });
        return Array.from(featureSet).sort((firstKey, secondKey) => {
            const firstLabel = FEATURE_LABELS[firstKey] || firstKey.replace(/_/g, ' ');
            const secondLabel = FEATURE_LABELS[secondKey] || secondKey.replace(/_/g, ' ');
            return firstLabel.localeCompare(secondLabel, undefined, { sensitivity: 'base' });
        });
    }, [activePlans]);

    const pendingPlan = plans.find(p => p.id === subscription?.pendingDowngradePlanId);

    // Invoice date filter
    const filteredInvoices = useMemo(() => {
        if (invoiceFilter === 'lifetime') return invoices;
        if (invoiceFilter === 'custom') {
            const from = filterFrom ? new Date(filterFrom) : null;
            const to = filterTo ? new Date(filterTo + 'T23:59:59') : null;
            return invoices.filter(inv => {
                const d = new Date(inv.createdAt);
                if (from && d < from) return false;
                if (to && d > to) return false;
                return true;
            });
        }
        const months = invoiceFilter === '1m' ? 1 : invoiceFilter === '3m' ? 3 : 6;
        const now = new Date();
        const cutoff = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
        return invoices.filter(inv => new Date(inv.createdAt) >= cutoff);
    }, [invoices, invoiceFilter, filterFrom, filterTo]);

    useEffect(() => { setInvoicePage(0); }, [invoiceFilter, filterFrom, filterTo]);

    const pagedInvoices = useMemo(
        () => filteredInvoices.slice(invoicePage * INVOICE_PAGE_SIZE, (invoicePage + 1) * INVOICE_PAGE_SIZE),
        [filteredInvoices, invoicePage]
    );
    const totalInvoicePages = Math.ceil(filteredInvoices.length / INVOICE_PAGE_SIZE);

    const fetchProration = useCallback(async (planId, couponCode) => {
        if (!currentOrgId || !planId) return;
        setLoadingProration(true);
        try {
            const data = await billingService.getProrationPreview(currentOrgId, planId, couponCode);
            if (data?.body) setProration(parseServiceBody(data.body));
        } catch (_) {
            // non-fatal
        } finally {
            setLoadingProration(false);
        }
    }, [currentOrgId]);

    const handleSelectPlan = useCallback((plan) => {
        if (!currentPlan) return;
        setSelectedPlanId(plan.id);
        setProration(null);
        setUpgradeCouponInput('');
        setUpgradeCouponApplied(null);
        setUpgradeCouponError(null);

        const isUpgrade = plan.priceMonthlyPaise > currentPlan.priceMonthlyPaise;
        if (isUpgrade) fetchProration(plan.id, null);
    }, [currentPlan, fetchProration]);

    useEffect(() => {
        if (!selectedPlanId || !selectedIsUpgrade) return;
        fetchProration(selectedPlanId, upgradeCouponApplied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upgradeCouponApplied]);

    const handleApplyCoupon = async () => {
        if (!upgradeCouponInput.trim()) return;
        setValidatingCoupon(true);
        setUpgradeCouponError(null);
        try {
            const data = await billingService.validateCoupon(upgradeCouponInput.trim());
            const result = parseServiceBody(data?.body);
            if (result?.valid) {
                setUpgradeCouponApplied(upgradeCouponInput.trim().toUpperCase());
            } else {
                setUpgradeCouponError(result?.invalidReason || 'Invalid coupon');
                setUpgradeCouponApplied(null);
            }
        } catch (_) {
            setUpgradeCouponError('Could not validate coupon');
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setUpgradeCouponApplied(null);
        setUpgradeCouponInput('');
        setUpgradeCouponError(null);
    };

    const handleUpgrade = async () => {
        if (!currentOrgId || !selectedPlanId || !selectedPlan) return;
        setConfirmingUpgrade(true);
        try {
            const orderData = await billingService.createUpgradeOrder(currentOrgId, selectedPlanId, upgradeCouponApplied);
            const order = parseServiceBody(orderData?.body);
            if (!order?.addonOrderId) {
                SnackbarManager.error('Failed to create upgrade order');
                setConfirmingUpgrade(false);
                return;
            }

            if (!order.razorpayOrderId || order.netPaise === 0) {
                const confirmData = await billingService.confirmUpgrade(
                    currentOrgId, order.addonOrderId, 'N/A', 'N/A', selectedPlanId, upgradeCouponApplied);
                if (isSuccessfulResponse(confirmData)) {
                    await refreshSubscription();
                    SnackbarManager.success(`Upgraded to ${selectedPlan.name}!`);
                    closePlanEditor();
                } else {
                    SnackbarManager.error('Upgrade confirmation failed');
                }
                setConfirmingUpgrade(false);
                return;
            }

            await loadRazorpayScript();
            if (!razorpayKeyRef.current || !window.Razorpay) {
                SnackbarManager.error('Payment gateway not ready. Please refresh and try again.');
                setConfirmingUpgrade(false);
                return;
            }

            const rzp = new window.Razorpay({
                key: razorpayKeyRef.current,
                order_id: order.razorpayOrderId,
                amount: order.netPaise,
                currency: 'INR',
                name: 'KaizIQ',
                description: `Upgrade to ${selectedPlan.name}`,
                theme: { color: '#6366f1' },
                handler: async (response) => {
                    try {
                        const confirmData = await billingService.confirmUpgrade(
                            currentOrgId,
                            order.addonOrderId,
                            response.razorpay_order_id,
                            response.razorpay_payment_id,
                            selectedPlanId,
                            upgradeCouponApplied
                        );
                        if (isSuccessfulResponse(confirmData)) {
                            await refreshSubscription();
                            SnackbarManager.success(`Upgraded to ${selectedPlan.name}!`);
                            closePlanEditor();
                        } else {
                            SnackbarManager.error('Payment received but upgrade failed. Please contact support.');
                        }
                    } catch (_) {
                        SnackbarManager.error('Upgrade confirmation failed. Please contact support.');
                    } finally {
                        setConfirmingUpgrade(false);
                    }
                },
                modal: { ondismiss: () => setConfirmingUpgrade(false) },
            });
            rzp.open();
        } catch (_) {
            SnackbarManager.error('Failed to initiate upgrade');
            setConfirmingUpgrade(false);
        }
    };

    const handleScheduleDowngrade = async () => {
        if (!currentOrgId || !selectedPlanId) return;
        setConfirmingDowngrade(true);
        try {
            const data = await billingService.scheduleDowngrade(currentOrgId, { newPlanId: selectedPlanId });
            if (isSuccessfulResponse(data)) {
                await refreshSubscription();
                SnackbarManager.success('Downgrade scheduled for end of billing period');
                setSelectedPlanId(null);
                setShowPlanEditor(false);
            } else {
                SnackbarManager.error(data?.message || 'Failed to schedule downgrade');
            }
        } catch (_) {
            SnackbarManager.error('Failed to schedule downgrade');
        } finally {
            setConfirmingDowngrade(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!currentOrgId) return;
        setConfirmingCancel(true);
        try {
            const data = await billingService.cancelSubscription(currentOrgId, {});
            if (isSuccessfulResponse(data)) {
                await refreshSubscription();
                SnackbarManager.success('Subscription cancelled. You keep access until end of billing period.');
                setShowCancelConfirm(false);
            } else {
                SnackbarManager.error(data?.message || 'Failed to cancel subscription');
            }
        } catch (_) {
            SnackbarManager.error('Failed to cancel subscription');
        } finally {
            setConfirmingCancel(false);
        }
    };

    const handleReactivate = async () => {
        if (!currentOrgId) return;
        setReactivating(true);
        try {
            const data = await billingService.reactivateSubscription(currentOrgId);
            if (isSuccessfulResponse(data)) {
                await refreshSubscription();
                SnackbarManager.success('Subscription reactivated successfully!');
            } else {
                SnackbarManager.error(data?.message || 'Failed to reactivate subscription');
            }
        } catch (_) {
            SnackbarManager.error('Failed to reactivate subscription');
        } finally {
            setReactivating(false);
        }
    };

    const handleRestart = async () => {
        if (!currentOrgId) return;
        setRestarting(true);
        try {
            const data = await billingService.restartSubscription(currentOrgId);
            if (isSuccessfulResponse(data)) {
                await refreshSubscription();
                const shortUrl = data?.data?.shortUrl;
                if (shortUrl) {
                    window.open(shortUrl, '_blank', 'noopener,noreferrer');
                }
                SnackbarManager.success('New subscription started — complete payment to activate it.');
            } else {
                SnackbarManager.error(data?.message || 'Failed to start a new subscription');
            }
        } catch (_) {
            SnackbarManager.error('Failed to start a new subscription');
        } finally {
            setRestarting(false);
        }
    };

    const handleDownloadInvoice = useCallback(async (invoice) => {
        setDownloadingInvoiceId(invoice.id);
        try {
            await billingService.downloadInvoice(invoice.id, invoice.invoiceNumber);
        } catch (_) {
            SnackbarManager.error('Failed to download invoice');
        } finally {
            setDownloadingInvoiceId(null);
        }
    }, []);

    const handleSaveProfile = async () => {
        if (!currentOrgId || !profileForm.billingName.trim()) {
            SnackbarManager.error('Legal name is required');
            return;
        }
        setSavingProfile(true);
        try {
            const request = {
                orgId: currentOrgId,
                billingName: profileForm.billingName.trim(),
                email: profileForm.email.trim() || null,
                address: {
                    line1: profileForm.addressLine1.trim() || null,
                    line2: profileForm.addressLine2.trim() || null,
                    city: profileForm.city.trim() || null,
                    state: profileForm.state.trim() || null,
                    pincode: profileForm.postalCode.trim() || null,
                    country: profileForm.country.trim() || 'IN',
                },
                gstNumber: profileForm.gstNumber.trim() || null,
                panNumber: profileForm.panNumber.trim() || null,
                vatNumber: profileForm.vatNumber.trim() || null,
            };
            const data = await billingService.upsertBillingProfile(request);
            if (!data?.error) {
                const fresh = await billingService.getBillingProfile(currentOrgId);
                if (fresh?.body) {
                    try {
                        const profile = parseServiceBody(fresh.body);
                        if (profile) setBillingProfile(profile);
                    } catch (_) {}
                }
                SnackbarManager.success('Billing profile saved');
                setEditingProfile(false);
            } else {
                SnackbarManager.error('Failed to save billing profile');
            }
        } catch (_) {
            SnackbarManager.error('Failed to save billing profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const updateProfileForm = (field, value) =>
        setProfileForm(prev => ({ ...prev, [field]: value }));

    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    const selectedIsUpgrade = selectedPlan && currentPlan
        ? selectedPlan.priceMonthlyPaise > currentPlan.priceMonthlyPaise
        : false;
    const selectedIsDowngrade = selectedPlan && currentPlan
        ? selectedPlan.priceMonthlyPaise < currentPlan.priceMonthlyPaise
        : false;

    const ticketPct = usage && usage.planLimitSnapshot > 0
        ? Math.min((usage.ticketCount / usage.planLimitSnapshot) * 100, 100)
        : 0;
    const ticketPctFillClass = ticketPct >= 100 ? ' --full' : ticketPct >= 80 ? ' --warn' : '';

    const isCancellable = subscription && !['CANCELLED'].includes(subscription.status);
    const isCancelled = subscription?.status === 'CANCELLED';
    const isGrowthPlan = currentPlan?.name?.toLowerCase() === 'growth';
    const periodEndPassed = subscription?.currentPeriodEnd
        ? new Date() > new Date(subscription.currentPeriodEnd)
        : false;
    const canUpgradeToRestart = isCancelled && !isGrowthPlan;
    const upgradablePlans = canUpgradeToRestart
        ? plans.filter(p => p.isActive && currentPlan && p.priceMonthlyPaise > currentPlan.priceMonthlyPaise)
        : [];

    const closePlanEditor = () => {
        setShowPlanEditor(false);
        setSelectedPlanId(null);
        setProration(null);
        setUpgradeCouponInput('');
        setUpgradeCouponApplied(null);
        setUpgradeCouponError(null);
    };

    const formatPlanLimit = (value, suffix = '') => {
        if (value === -1) return 'Unlimited';
        if (value == null) return '—';
        return `${value}${suffix}`;
    };

    const formatFeatureLabel = (featureKey) => {
        if (FEATURE_LABELS[featureKey]) return FEATURE_LABELS[featureKey];
        return featureKey
            .split('_')
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <div className={className}>

            {/* ── Tab Bar ────────────────────────────────────────────── */}
            <div className={className + '__tabs'}>
                <button
                    className={`${className}__tab${activeTab === 'plan' ? ' --active' : ''}`}
                    onClick={() => setActiveTab('plan')}
                >
                    Plan
                </button>
                <button
                    className={`${className}__tab${activeTab === 'billing' ? ' --active' : ''}`}
                    onClick={() => setActiveTab('billing')}
                >
                    Billing
                </button>
                <button
                    className={`${className}__tab${activeTab === 'profile' ? ' --active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    Profile
                </button>
            </div>

            {/* ══════════════════════════════════════════════════════════
                PLAN TAB
            ══════════════════════════════════════════════════════════ */}
            {activeTab === 'plan' && (
                <>
                    {/* ── Current Plan ──────────────────────────────────── */}
                    <section className={className + '__section'}>
                        {!subscription ? (
                            <div className={className + '__emptyState'}>No active subscription found.</div>
                        ) : (
                            <div className={className + '__accountCard'}>
                                {subscription.status === 'CANCELLED' && (
                                    <div className={className + '__accountCard__alert'}>
                                        <IoAlertCircleOutline />
                                        <span>
                                            Subscription <strong>cancelled</strong> — full access until{' '}
                                            <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
                                            After this date your workspace will be locked.
                                        </span>
                                    </div>
                                )}

                                {subscription.status === 'HALTED' && (
                                    <div className={className + '__accountCard__alert'}>
                                        <IoAlertCircleOutline />
                                        <span>
                                            We were unable to process your last payment. Start a new subscription to restore access.
                                        </span>
                                        <button
                                            type="button"
                                            className={className + '__accountCard__alert__action'}
                                            onClick={handleRestart}
                                            disabled={restarting}
                                        >
                                            {restarting ? 'Starting…' : 'Try Again'}
                                        </button>
                                    </div>
                                )}
                                <div className={className + '__accountCard__body'}>
                                    <div className={className + '__accountCard__left'}>
                                        <span className={className + '__accountCard__eyebrow'}>Current Plan</span>
                                        <span className={className + '__accountCard__planName'}>{currentPlan?.name || '—'}</span>
                                        <span className={className + '__accountCard__cycle'}>
                                            {subscription.billingCycle === 'ANNUAL' ? 'Annual billing' : 'Monthly billing'}
                                        </span>
                                        {pendingPlan && (
                                            <div className={className + '__accountCard__pending'}>
                                                <IoArrowDownCircleOutline />
                                                Downgrade to <strong>{pendingPlan.name}</strong> scheduled at period end
                                            </div>
                                        )}
                                    </div>
                                    <div className={className + '__accountCard__right'}>
                                        <span
                                            className={className + '__statement__statusBadge'}
                                            style={{ '--status-color': statusMeta.color }}
                                        >
                                            {statusMeta.label}
                                        </span>
                                        <div className={className + '__accountCard__renewal'}>
                                            <span className={className + '__accountCard__renewalLabel'}>
                                                {subscription.status === 'CANCELLED' ? 'Access ends' : 'Renews'}
                                            </span>
                                            <span className={className + '__accountCard__renewalDate'}>
                                                {formatDate(subscription.currentPeriodEnd)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── Restart Subscription (only when CANCELLED) ────── */}
                    {isCancelled && (
                        <section className={className + '__section'}>
                            <h2 className={className + '__sectionTitle'}>Restart Subscription</h2>

                            <div className={className + '__restartCard'}>
                                <div className={className + '__restartCard__info'}>
                                    <div className={className + '__restartCard__title'}>
                                        Restart {currentPlan?.name || 'current'} plan
                                    </div>
                                    <div className={className + '__restartCard__desc'}>
                                        {periodEndPassed
                                            ? 'Your billing period has ended. You can restart your plan now.'
                                            : <>You must wait until your current billing period ends.
                                                {' '}Restart available from{' '}
                                                <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.</>
                                        }
                                    </div>
                                </div>
                                <button
                                    className={`${className}__btn ${periodEndPassed ? '--primary' : '--ghost'}`}
                                    onClick={periodEndPassed ? handleReactivate : undefined}
                                    disabled={!periodEndPassed || reactivating}
                                >
                                    {reactivating
                                        ? 'Restarting…'
                                        : periodEndPassed
                                            ? 'Restart Plan'
                                            : `Available ${formatDate(subscription.currentPeriodEnd)}`
                                    }
                                </button>
                            </div>

                            {canUpgradeToRestart && upgradablePlans.length > 0 && (
                                <div className={className + '__restartUpgrade'}>
                                    <div className={className + '__restartUpgrade__header'}>
                                        <IoArrowUpCircleOutline />
                                        <span>Or upgrade now to restart immediately</span>
                                    </div>

                                    <div className={className + '__planGrid'}>
                                        {upgradablePlans.map(plan => (
                                            <div
                                                key={plan.id}
                                                className={`${className}__planOption${plan.id === selectedPlanId ? ' is-selected' : ''}`}
                                                onClick={() => handleSelectPlan(plan)}
                                            >
                                                <div className={className + '__planOption__name'}>{plan.name}</div>
                                                <div className={className + '__planOption__price'}>
                                                    {paise2Rupees(plan.priceMonthlyPaise)}<span>/mo</span>
                                                </div>
                                                <div className={className + '__planOption__direction'}>
                                                    <IoArrowUpCircleOutline /> Upgrade & Restart
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {selectedPlan && selectedIsUpgrade && (
                                        <div className={className + '__actionPanel'}>
                                            <div className={className + '__actionPanel__title'}>
                                                Upgrade & restart with <strong>{selectedPlan.name}</strong>
                                            </div>
                                            {loadingProration ? (
                                                <Skeleton variant="rounded" height={64} />
                                            ) : proration ? (
                                                <div className={className + '__prorationBox'}>
                                                    <div className={className + '__prorationBox__row'}>
                                                        <span>Credit for remaining {proration.daysRemaining} days on current plan</span>
                                                        <span className="--credit">−{paise2Rupees(proration.creditPaise)}</span>
                                                    </div>
                                                    <div className={className + '__prorationBox__row'}>
                                                        <span>Charge for remaining {proration.daysRemaining} days on new plan</span>
                                                        <span>+{paise2Rupees(proration.chargePaise)}</span>
                                                    </div>
                                                    <div className={className + '__prorationBox__row --total'}>
                                                        <span>Amount due today</span>
                                                        <strong>{paise2Rupees(proration.netPaise)}</strong>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div className={className + '__actionPanel__note'}>
                                                Upgrading will restart your subscription immediately on the {selectedPlan.name} plan.
                                            </div>
                                            <div className={className + '__actionPanel__buttons'}>
                                                <button
                                                    className={className + '__btn --primary'}
                                                    onClick={handleUpgrade}
                                                    disabled={confirmingUpgrade}
                                                >
                                                    {confirmingUpgrade ? 'Processing…' : 'Upgrade & Restart'}
                                                </button>
                                                <button
                                                    className={className + '__btn --ghost'}
                                                    onClick={() => { setSelectedPlanId(null); setProration(null); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {/* ── Current Usage ─────────────────────────────────── */}
                    <section className={className + '__section'}>
                        {usage && usage.planLimitSnapshot > 0 && usage.ticketCount >= usage.planLimitSnapshot && (
                            <div className={className + '__overageBanner'}>
                                <IoWarningOutline />
                                <span>
                                    You've reached your <strong>{usage.planLimitSnapshot}-ticket</strong> monthly limit.
                                    All additional tickets this month will be counted as <strong>overage</strong> and billed accordingly.
                                </span>
                            </div>
                        )}
                        {loadingUsage ? (
                            <Skeleton variant="rounded" height={120} />
                        ) : !usage ? (
                            <div className={className + '__emptyState'}>Usage data unavailable.</div>
                        ) : (
                            <div className={className + '__usageCard'}>
                                <div className={className + '__usageCard__header'}>
                                    <span className={className + '__usageCard__title'}>Current Usage</span>
                                    <span className={className + '__usageCard__month'}>{usage.billingMonth || '—'}</span>
                                </div>
                                <div className={className + '__usageCard__strip'}>
                                    <div className={className + '__usageCard__stripItem'}>
                                        <span className={className + '__usageCard__stripLabel'}>Tickets Used</span>
                                        <span className={className + '__usageCard__stripValue'}>
                                            {usage.ticketCount ?? 0}
                                            <span className={className + '__usageCard__stripLimit'}>
                                                {usage.planLimitSnapshot === -1
                                                    ? ' / ∞'
                                                    : ` / ${usage.planLimitSnapshot}`}
                                            </span>
                                        </span>
                                    </div>
                                    <div className={className + '__usageCard__stripItem'}>
                                        <span className={className + '__usageCard__stripLabel'}>Overage Tickets</span>
                                        <span className={`${className}__usageCard__stripValue${usage.overageCount > 0 ? ' --warn' : ''}`}>
                                            {usage.overageCount ?? 0}
                                        </span>
                                    </div>
                                    <div className={className + '__usageCard__stripItem'}>
                                        <span className={className + '__usageCard__stripLabel'}>Overage Charges</span>
                                        <span className={`${className}__usageCard__stripValue${usage.overageChargedPaise > 0 ? ' --warn' : ''}`}>
                                            {paise2Rupees(usage.overageChargedPaise ?? 0)}
                                        </span>
                                    </div>
                                    {usage.planLimitSnapshot > 0 && (
                                        <div className={`${className}__usageCard__stripItem --progress`}>
                                            <span className={className + '__usageCard__stripLabel'}>Ticket Quota</span>
                                            <div className={className + '__usageCard__bar'}>
                                                <div
                                                    className={`${className}__usageCard__barFill${ticketPctFillClass}`}
                                                    style={{ width: `${ticketPct}%` }}
                                                />
                                            </div>
                                            <span className={className + '__usageCard__barPct'}>{Math.round(ticketPct)}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── Change Plan (hidden when cancelled) ──────────── */}
                    {!isCancelled && (
                        <section className={className + '__section'}>
                            <div className={className + '__changePlanRow'}>
                                <div className={className + '__changePlanRow__info'}>
                                    <span className={className + '__changePlanRow__title'}>Change Plan</span>
                                    <span className={className + '__changePlanRow__desc'}>
                                        Compare all plans, see your current tier, and make upgrades or downgrades.
                                    </span>
                                </div>
                                <button
                                    className={className + '__btn --primary'}
                                    onClick={() => setShowPlanEditor(true)}
                                    disabled={loadingPlans || activePlans.length === 0}
                                >
                                    {loadingPlans ? 'Loading…' : 'Edit Plan'}
                                </button>
                            </div>
                        </section>
                    )}

                    {/* ── Danger Zone ───────────────────────────────────── */}
                    {isCancellable && (
                        <section className={className + '__section'}>
                            <h2 className={`${className}__sectionTitle --danger`}>Danger Zone</h2>
                            <div className={className + '__dangerZone'}>
                                <div className={className + '__dangerZone__row'}>
                                    <div className={className + '__dangerZone__info'}>
                                        <span className={className + '__dangerZone__title'}>Cancel Subscription</span>
                                        <span className={className + '__dangerZone__desc'}>
                                            Stops future payments immediately. You keep full access until{' '}
                                            <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
                                            No refunds will be issued.
                                        </span>
                                    </div>
                                    {!showCancelConfirm && (
                                        <button
                                            className={className + '__btn --danger'}
                                            onClick={() => setShowCancelConfirm(true)}
                                        >
                                            Cancel Subscription
                                        </button>
                                    )}
                                </div>

                                {showCancelConfirm && (
                                    <div className={className + '__cancelConfirm'}>
                                        <div className={className + '__cancelConfirm__warning'}>
                                            <IoWarningOutline />
                                            <span>
                                                This cannot be undone. All future payments will be stopped.
                                                No refund will be issued for the current period.
                                                You will retain access until <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
                                            </span>
                                        </div>
                                        <div className={className + '__actionPanel__buttons'}>
                                            <button
                                                className={className + '__btn --danger'}
                                                onClick={handleCancelSubscription}
                                                disabled={confirmingCancel}
                                            >
                                                {confirmingCancel ? 'Cancelling…' : 'Yes, Cancel My Subscription'}
                                            </button>
                                            <button
                                                className={className + '__btn --ghost'}
                                                onClick={() => setShowCancelConfirm(false)}
                                            >
                                                Keep My Plan
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </>
            )}

            {/* ══════════════════════════════════════════════════════════
                BILLING TAB
            ══════════════════════════════════════════════════════════ */}
            {activeTab === 'billing' && (
                <>
                    {/* ── Statement Header ──────────────────────────────── */}
                    <section className={className + '__section'}>
                        {!subscription ? (
                            <div className={className + '__emptyState'}>No active subscription found.</div>
                        ) : (
                            <div className={className + '__statement'}>
                                <div className={className + '__statement__top'}>
                                    <div className={className + '__statement__identity'}>
                                        <span className={className + '__statement__eyebrow'}>Current Plan</span>
                                        <span className={className + '__statement__planName'}>{currentPlan?.name || '—'}</span>
                                    </div>
                                    <div className={className + '__statement__badges'}>
                                        <span className={className + '__statement__cyclePill'}>
                                            {subscription.billingCycle === 'ANNUAL' ? 'Annual' : 'Monthly'}
                                        </span>
                                        <span
                                            className={className + '__statement__statusBadge'}
                                            style={{ '--status-color': statusMeta.color }}
                                        >
                                            {statusMeta.label}
                                        </span>
                                    </div>
                                </div>
                                <div className={className + '__statement__strip'}>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>Period Start</span>
                                        <span className={`${className}__statement__stripValue --mono`}>{formatDate(subscription.currentPeriodStart)}</span>
                                    </div>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>
                                            {subscription.status === 'CANCELLED' ? 'Access Until' : 'Next Renewal'}
                                        </span>
                                        <span className={`${className}__statement__stripValue --mono`}>{formatDate(subscription.currentPeriodEnd)}</span>
                                    </div>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>Agent Seats</span>
                                        <span className={`${className}__statement__stripValue`}>{subscription.agentSeatCount ?? '—'}</span>
                                    </div>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>Monthly Price</span>
                                        <span className={`${className}__statement__stripValue --mono`}>{paise2Rupees(currentPlan?.priceMonthlyPaise)}</span>
                                    </div>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>Annual Price</span>
                                        <span className={`${className}__statement__stripValue --mono`}>{paise2Rupees(currentPlan?.priceAnnualPaise)}</span>
                                    </div>
                                    <div className={className + '__statement__stripItem'}>
                                        <span className={className + '__statement__stripLabel'}>Ticket Limit</span>
                                        <span className={`${className}__statement__stripValue`}>{formatPlanLimit(currentPlan?.ticketLimitMonthly, '/mo')}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── Payment Ledger ────────────────────────────────── */}
                    <section className={className + '__section'}>
                        <div className={className + '__sectionTitleRow'}>
                            <h2 className={className + '__sectionTitle'}>Payment History</h2>
                            {!loadingInvoices && filteredInvoices.length > 0 && (
                                <span className={className + '__ledger__count'}>
                                    {filteredInvoices.length} record{filteredInvoices.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>

                        {/* Filter bar */}
                        <div className={className + '__invoiceFilter'}>
                            <div className={className + '__invoiceFilter__pills'}>
                                {[
                                    { key: 'lifetime', label: 'Lifetime' },
                                    { key: '1m',       label: 'Last month' },
                                    { key: '3m',       label: 'Last 3 months' },
                                    { key: '6m',       label: 'Last 6 months' },
                                    { key: 'custom',   label: 'Custom' },
                                ].map(opt => (
                                    <button
                                        key={opt.key}
                                        className={`${className}__invoiceFilter__pill${invoiceFilter === opt.key ? ' --active' : ''}`}
                                        onClick={() => setInvoiceFilter(opt.key)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            {invoiceFilter === 'custom' && (
                                <div className={className + '__invoiceFilter__custom'}>
                                    <label className={className + '__invoiceFilter__customLabel'}>From</label>
                                    <input
                                        className={className + '__invoiceFilter__date'}
                                        type="date"
                                        value={filterFrom}
                                        onChange={e => setFilterFrom(e.target.value)}
                                    />
                                    <label className={className + '__invoiceFilter__customLabel'}>To</label>
                                    <input
                                        className={className + '__invoiceFilter__date'}
                                        type="date"
                                        value={filterTo}
                                        onChange={e => setFilterTo(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>

                        {loadingInvoices ? (
                            <div className={className + '__invoiceList'}>
                                {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={44} />)}
                            </div>
                        ) : filteredInvoices.length === 0 ? (
                            <div className={className + '__emptyState'}>
                                {invoices.length === 0 ? 'No invoices found.' : 'No invoices in the selected period.'}
                            </div>
                        ) : (
                            <>
                                <div className={className + '__ledger'}>
                                    <table className={className + '__ledgerTable'}>
                                        <thead>
                                            <tr>
                                                <th className={className + '__ledgerTable__th'}>Invoice</th>
                                                <th className={className + '__ledgerTable__th'}>Type</th>
                                                <th className={className + '__ledgerTable__th'}>Period</th>
                                                <th className={`${className}__ledgerTable__th --right`}>Amount</th>
                                                <th className={className + '__ledgerTable__th'}>Status</th>
                                                <th className={`${className}__ledgerTable__th --center`} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedInvoices.map(inv => (
                                                <tr key={inv.id} className={className + '__ledgerTable__row'}>
                                                    <td className={`${className}__ledgerTable__td --id`}>
                                                        {inv.invoiceNumber || `#${inv.id}`}
                                                    </td>
                                                    <td className={className + '__ledgerTable__td'}>
                                                        <span className={`${className}__invoiceTypeBadge${inv.invoiceType === 'PRORATION' ? ' --proration' : ''}`}>
                                                            {inv.invoiceType === 'PRORATION' ? 'Proration' : 'Subscription'}
                                                        </span>
                                                    </td>
                                                    <td className={`${className}__ledgerTable__td --muted`}>
                                                        {formatDate(inv.billingPeriodStart)}
                                                    </td>
                                                    <td className={`${className}__ledgerTable__td --amount`}>
                                                        {paise2Rupees(inv.totalAmountPaise)}
                                                    </td>
                                                    <td className={className + '__ledgerTable__td'}>
                                                        <span
                                                            className={className + '__invoiceRow__status'}
                                                            data-status={inv.status?.toLowerCase()}
                                                        >
                                                            {inv.status}
                                                        </span>
                                                    </td>
                                                    <td className={`${className}__ledgerTable__td --center`}>
                                                        <Tooltip title="Download invoice">
                                                            <button
                                                                className={className + '__invoiceRow__download'}
                                                                onClick={() => handleDownloadInvoice(inv)}
                                                                disabled={downloadingInvoiceId === inv.id}
                                                            >
                                                                {downloadingInvoiceId === inv.id
                                                                    ? <IoRefreshOutline className="spin" />
                                                                    : <IoDownloadOutline />
                                                                }
                                                            </button>
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalInvoicePages > 1 && (
                                    <div className={className + '__pagination'}>
                                        <button
                                            className={className + '__pagination__btn'}
                                            onClick={() => setInvoicePage(p => p - 1)}
                                            disabled={invoicePage === 0}
                                        >
                                            Previous
                                        </button>
                                        <span className={className + '__pagination__info'}>
                                            Page {invoicePage + 1} of {totalInvoicePages}
                                        </span>
                                        <button
                                            className={className + '__pagination__btn'}
                                            onClick={() => setInvoicePage(p => p + 1)}
                                            disabled={invoicePage >= totalInvoicePages - 1}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                </>
            )}

            {/* ══════════════════════════════════════════════════════════
                PROFILE TAB
            ══════════════════════════════════════════════════════════ */}
            {activeTab === 'profile' && (
                <section className={className + '__section'}>
                    <div className={className + '__sectionTitleRow'}>
                        <h2 className={className + '__sectionTitle'}>Payment Profile</h2>
                        {!loadingProfile && !editingProfile && (
                            <button
                                className={className + '__btn --ghost --sm'}
                                onClick={() => setEditingProfile(true)}
                            >
                                {billingProfile ? 'Edit' : 'Set up'}
                            </button>
                        )}
                    </div>

                    {loadingProfile ? (
                        <Skeleton variant="rounded" height={160} />
                    ) : editingProfile ? (
                        <div className={className + '__profileForm'}>
                            <div className={className + '__profileForm__grid'}>
                                <div className={className + '__profileForm__field --span2'}>
                                    <label className={className + '__profileForm__label'}>Legal Name <span className="--req">*</span></label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="Your organisation's legal name"
                                        value={profileForm.billingName}
                                        onChange={e => updateProfileForm('billingName', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field'}>
                                    <label className={className + '__profileForm__label'}>Billing Email</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="email"
                                        placeholder="billing@example.com"
                                        value={profileForm.email}
                                        onChange={e => updateProfileForm('email', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field'}>
                                    <label className={className + '__profileForm__label'}>Country</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="IN"
                                        value={profileForm.country}
                                        onChange={e => updateProfileForm('country', e.target.value)}
                                        disabled={savingProfile}
                                        maxLength={2}
                                    />
                                </div>
                                <div className={className + '__profileForm__field --span2'}>
                                    <label className={className + '__profileForm__label'}>Address Line 1</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="Street address, building"
                                        value={profileForm.addressLine1}
                                        onChange={e => updateProfileForm('addressLine1', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field --span2'}>
                                    <label className={className + '__profileForm__label'}>Address Line 2</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="Floor, suite, landmark (optional)"
                                        value={profileForm.addressLine2}
                                        onChange={e => updateProfileForm('addressLine2', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field'}>
                                    <label className={className + '__profileForm__label'}>City</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="City"
                                        value={profileForm.city}
                                        onChange={e => updateProfileForm('city', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field'}>
                                    <label className={className + '__profileForm__label'}>State</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="State / Province"
                                        value={profileForm.state}
                                        onChange={e => updateProfileForm('state', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                                <div className={className + '__profileForm__field'}>
                                    <label className={className + '__profileForm__label'}>Postal Code</label>
                                    <input
                                        className={className + '__profileForm__input'}
                                        type="text"
                                        placeholder="PIN / ZIP"
                                        value={profileForm.postalCode}
                                        onChange={e => updateProfileForm('postalCode', e.target.value)}
                                        disabled={savingProfile}
                                    />
                                </div>
                            </div>

                            <div className={className + '__profileForm__taxSection'}>
                                <span className={className + '__profileForm__taxTitle'}>Tax Identifiers</span>
                                <div className={className + '__profileForm__grid'}>
                                    <div className={className + '__profileForm__field'}>
                                        <label className={className + '__profileForm__label'}>GST Number</label>
                                        <input
                                            className={className + '__profileForm__input --mono'}
                                            type="text"
                                            placeholder="22AAAAA0000A1Z5"
                                            value={profileForm.gstNumber}
                                            onChange={e => updateProfileForm('gstNumber', e.target.value.toUpperCase())}
                                            disabled={savingProfile}
                                        />
                                    </div>
                                    <div className={className + '__profileForm__field'}>
                                        <label className={className + '__profileForm__label'}>PAN Number</label>
                                        <input
                                            className={className + '__profileForm__input --mono'}
                                            type="text"
                                            placeholder="AAAAA0000A"
                                            value={profileForm.panNumber}
                                            onChange={e => updateProfileForm('panNumber', e.target.value.toUpperCase())}
                                            disabled={savingProfile}
                                        />
                                    </div>
                                    <div className={className + '__profileForm__field'}>
                                        <label className={className + '__profileForm__label'}>VAT / Tax ID</label>
                                        <input
                                            className={className + '__profileForm__input --mono'}
                                            type="text"
                                            placeholder="International tax identifier"
                                            value={profileForm.vatNumber}
                                            onChange={e => updateProfileForm('vatNumber', e.target.value)}
                                            disabled={savingProfile}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={className + '__profileForm__actions'}>
                                <button
                                    className={className + '__btn --primary'}
                                    onClick={handleSaveProfile}
                                    disabled={savingProfile || !profileForm.billingName.trim()}
                                >
                                    {savingProfile ? 'Saving…' : 'Save Profile'}
                                </button>
                                <button
                                    className={className + '__btn --ghost'}
                                    onClick={() => {
                                        setEditingProfile(false);
                                        if (billingProfile) {
                                            setProfileForm({
                                                billingName: billingProfile.legalName || '',
                                                email: billingProfile.billingEmail || '',
                                                addressLine1: billingProfile.addressLine1 || '',
                                                addressLine2: billingProfile.addressLine2 || '',
                                                city: billingProfile.city || '',
                                                state: billingProfile.state || '',
                                                postalCode: billingProfile.postalCode || '',
                                                country: billingProfile.countryCode || 'IN',
                                                gstNumber: billingProfile.gstNumber || '',
                                                panNumber: billingProfile.panNumber || '',
                                                vatNumber: billingProfile.vatNumber || '',
                                            });
                                        }
                                    }}
                                    disabled={savingProfile}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : !billingProfile ? (
                        <div className={className + '__profileEmpty'}>
                            <span className={className + '__profileEmpty__text'}>
                                No billing profile set up yet. Add your organisation's billing details to have them appear on invoices.
                            </span>
                            <button
                                className={className + '__btn --ghost --sm'}
                                onClick={() => setEditingProfile(true)}
                            >
                                Set up profile
                            </button>
                        </div>
                    ) : (
                        <div className={className + '__profileCard'}>
                            <div className={className + '__profileCard__section'}>
                                <div className={className + '__profileCard__strip'}>
                                    <div className={`${className}__profileCard__item --wide`}>
                                        <span className={className + '__profileCard__label'}>Legal Name</span>
                                        <span className={className + '__profileCard__value'}>{billingProfile.legalName || '—'}</span>
                                    </div>
                                    <div className={className + '__profileCard__item'}>
                                        <span className={className + '__profileCard__label'}>Billing Email</span>
                                        <span className={className + '__profileCard__value'}>{billingProfile.billingEmail || '—'}</span>
                                    </div>
                                    <div className={className + '__profileCard__item'}>
                                        <span className={className + '__profileCard__label'}>Country</span>
                                        <span className={className + '__profileCard__value'}>{billingProfile.countryCode || '—'}</span>
                                    </div>
                                    <div className={className + '__profileCard__item'}>
                                        <span className={className + '__profileCard__label'}>Tax Rate</span>
                                        <span className={className + '__profileCard__value'}>{billingProfile.taxRatePct ?? 0}% GST</span>
                                    </div>
                                </div>
                            </div>
                            {(billingProfile.addressLine1 || billingProfile.city) && (
                                <div className={className + '__profileCard__section'}>
                                    <span className={className + '__profileCard__sectionLabel'}>Address</span>
                                    <span className={className + '__profileCard__address'}>
                                        {[
                                            billingProfile.addressLine1,
                                            billingProfile.addressLine2,
                                            billingProfile.city,
                                            billingProfile.state,
                                            billingProfile.postalCode,
                                        ].filter(Boolean).join(', ')}
                                    </span>
                                </div>
                            )}
                            {(billingProfile.gstNumber || billingProfile.panNumber || billingProfile.vatNumber) && (
                                <div className={className + '__profileCard__section'}>
                                    <div className={className + '__profileCard__strip'}>
                                        {billingProfile.gstNumber && (
                                            <div className={className + '__profileCard__item'}>
                                                <span className={className + '__profileCard__label'}>GST Number</span>
                                                <span className={`${className}__profileCard__value --mono`}>{billingProfile.gstNumber}</span>
                                            </div>
                                        )}
                                        {billingProfile.panNumber && (
                                            <div className={className + '__profileCard__item'}>
                                                <span className={className + '__profileCard__label'}>PAN Number</span>
                                                <span className={`${className}__profileCard__value --mono`}>{billingProfile.panNumber}</span>
                                            </div>
                                        )}
                                        {billingProfile.vatNumber && (
                                            <div className={className + '__profileCard__item'}>
                                                <span className={className + '__profileCard__label'}>VAT / Tax ID</span>
                                                <span className={`${className}__profileCard__value --mono`}>{billingProfile.vatNumber}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            )}

            {/* ── Plan Editor Modal (always mounted) ─────────────── */}
            <Modal open={showPlanEditor} onClose={closePlanEditor}>
                <div
                    className={className + '__planEditorOverlay'}
                    onClick={closePlanEditor}
                    style={{
                        '--kzq-card': 'var(--color-componentBackground)',
                        '--kzq-bg': 'var(--color-background)',
                        '--kzq-border': 'var(--color-border)',
                        '--kzq-text': 'var(--color-text)',
                        '--kzq-muted': 'var(--color-primaryGrey)',
                        '--kzq-accent': 'var(--color-primaryBlue)',
                        '--kzq-shadow': 'var(--color-shadow)',
                    }}
                >
                    <div className={className + '__planEditorModal'} onClick={(event) => event.stopPropagation()}>

                        <div className={className + '__planEditorModal__header'}>
                            <div className={className + '__planEditorModal__headerLeft'}>
                                <span className={className + '__planEditorModal__eyebrow'}>Plan Management</span>
                                <h3 className={className + '__planEditorModal__title'}>Edit Plan</h3>
                            </div>
                            <button
                                className={className + '__planEditorModal__close'}
                                onClick={closePlanEditor}
                                aria-label="Close plan editor"
                            >
                                <IoCloseOutline />
                            </button>
                        </div>

                        <div className={className + '__planSelectorStrip'}>
                            {loadingPlans ? (
                                [1, 2, 3, 4].map(i => (
                                    <div key={i} className={className + '__planRail'} style={{ pointerEvents: 'none' }}>
                                        <Skeleton variant="rounded" height={68} />
                                    </div>
                                ))
                            ) : activePlans.map(plan => {
                                const isCurrent = plan.id === currentPlan?.id;
                                const isPendingDowngrade = plan.id === subscription?.pendingDowngradePlanId;
                                const isSelected = plan.id === selectedPlanId;
                                const isEnterprise = plan.name?.toLowerCase() === 'enterprise';
                                const isUpgrade = currentPlan ? plan.priceMonthlyPaise > currentPlan.priceMonthlyPaise : false;
                                return (
                                    <div
                                        key={plan.id}
                                        className={[
                                            className + '__planRail',
                                            isCurrent ? 'is-current' : '',
                                            isSelected ? 'is-selected' : '',
                                            isPendingDowngrade ? 'is-pending' : '',
                                            isEnterprise && !isCurrent ? 'is-enterprise' : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => {
                                            if (isCurrent) return;
                                            if (isEnterprise) { window.open('mailto:sales@kaiziq.com', '_blank'); return; }
                                            handleSelectPlan(plan);
                                        }}
                                    >
                                        <div className={className + '__planRail__name'}>
                                            {plan.name}
                                            {isCurrent && <span className={className + '__planRail__tag'}>Current</span>}
                                            {isPendingDowngrade && <span className={className + '__planRail__tag --pending'}>Pending</span>}
                                        </div>
                                        <div className={className + '__planRail__price'}>
                                            {isEnterprise
                                                ? <span className={className + '__planRail__priceCustom'}>Custom</span>
                                                : <>{paise2Rupees(plan.priceMonthlyPaise)}<span className={className + '__planRail__priceSuffix'}>/mo</span></>
                                            }
                                        </div>
                                        {!isCurrent && (
                                            <div className={className + '__planRail__dir'}>
                                                {isEnterprise
                                                    ? 'Contact Sales'
                                                    : isUpgrade
                                                        ? <><IoArrowUpCircleOutline /> Upgrade</>
                                                        : <><IoArrowDownCircleOutline /> Downgrade</>
                                                }
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {!loadingPlans && (
                            <div className={className + '__planTableWrap'}>
                                <table className={className + '__planCompareTable'}>
                                    <thead>
                                        <tr>
                                            <th className={className + '__planCompareTable__featureCol'}>Feature</th>
                                            {activePlans.map(plan => (
                                                <th
                                                    key={plan.id}
                                                    className={[
                                                        plan.id === selectedPlanId ? 'is-selected-col' : '',
                                                        plan.id === currentPlan?.id ? 'is-current-col' : '',
                                                    ].filter(Boolean).join(' ')}
                                                >
                                                    {plan.name}
                                                    {plan.id === currentPlan?.id && (
                                                        <span className={className + '__planCompareCurrentTag'}>Current</span>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className={className + '__planCompareTable__rowLabel'}>Monthly Price</td>
                                            {activePlans.map(plan => (
                                                <td key={`${plan.id}-monthly`} className={plan.id === selectedPlanId ? 'is-selected-col' : plan.id === currentPlan?.id ? 'is-current-col' : ''}>
                                                    {plan.name?.toLowerCase() === 'enterprise' ? 'Custom' : paise2Rupees(plan.priceMonthlyPaise)}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className={className + '__planCompareTable__rowLabel'}>Annual Price</td>
                                            {activePlans.map(plan => (
                                                <td key={`${plan.id}-annual`} className={plan.id === selectedPlanId ? 'is-selected-col' : plan.id === currentPlan?.id ? 'is-current-col' : ''}>
                                                    {plan.name?.toLowerCase() === 'enterprise' ? 'Custom' : paise2Rupees(plan.priceAnnualPaise)}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className={className + '__planCompareTable__rowLabel'}>Agents</td>
                                            {activePlans.map(plan => (
                                                <td key={`${plan.id}-agents`} className={plan.id === selectedPlanId ? 'is-selected-col' : plan.id === currentPlan?.id ? 'is-current-col' : ''}>
                                                    {formatPlanLimit(plan.maxAgents, ' users')}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className={className + '__planCompareTable__rowLabel'}>Tickets / month</td>
                                            {activePlans.map(plan => (
                                                <td key={`${plan.id}-tickets`} className={plan.id === selectedPlanId ? 'is-selected-col' : plan.id === currentPlan?.id ? 'is-current-col' : ''}>
                                                    {formatPlanLimit(plan.ticketLimitMonthly, ' tickets')}
                                                </td>
                                            ))}
                                        </tr>
                                        {comparisonFeatureKeys.map(featureKey => (
                                            <tr key={featureKey}>
                                                <td className={className + '__planCompareTable__rowLabel'}>{formatFeatureLabel(featureKey)}</td>
                                                {activePlans.map(plan => {
                                                    const isEnabled = !!plan?.features?.[featureKey];
                                                    return (
                                                        <td
                                                            key={`${plan.id}-${featureKey}`}
                                                            className={[
                                                                plan.id === selectedPlanId ? 'is-selected-col' : plan.id === currentPlan?.id ? 'is-current-col' : '',
                                                                className + '__featureCell',
                                                            ].filter(Boolean).join(' ')}
                                                        >
                                                            {isEnabled
                                                                ? <span className={className + '__featureCheck'}><IoCheckmarkCircleOutline /></span>
                                                                : <span className={className + '__featureDash'}>—</span>
                                                            }
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {selectedPlan && (
                            <div className={className + '__planEditorFooter'}>
                                <div className={className + '__planEditorFooter__info'}>
                                    <span className={className + '__planEditorFooter__label'}>
                                        {selectedIsUpgrade ? 'Upgrading to' : 'Downgrading to'}
                                    </span>
                                    <strong className={className + '__planEditorFooter__planName'}>{selectedPlan.name}</strong>

                                    {selectedIsUpgrade && loadingProration && (
                                        <Skeleton variant="rounded" width={200} height={20} />
                                    )}
                                    {selectedIsUpgrade && !loadingProration && proration && (
                                        <div className={className + '__planEditorFooter__proration'}>
                                            <span className={className + '__planEditorFooter__prorationItem'}>
                                                Credit <span className="--credit">−{paise2Rupees(proration.creditPaise)}</span>
                                            </span>
                                            <span className={className + '__planEditorFooter__sep'}>·</span>
                                            <span className={className + '__planEditorFooter__prorationItem --total'}>
                                                Due today <strong>{paise2Rupees(proration.netPaise)}</strong>
                                            </span>
                                            {proration.newEffectivePricePaise != null && proration.newEffectivePricePaise !== selectedPlan.priceMonthlyPaise && (
                                                <span className={className + '__planEditorFooter__prorationItem --discount'}>
                                                    then {paise2Rupees(proration.newEffectivePricePaise)}/mo
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {selectedIsUpgrade && !loadingProration && (
                                        <div className={className + '__planEditorFooter__coupon'}>
                                            {upgradeCouponApplied ? (
                                                <div className={className + '__planEditorFooter__couponApplied'}>
                                                    <IoCheckmarkCircleOutline />
                                                    <span>Coupon <strong>{upgradeCouponApplied}</strong> applied</span>
                                                    <button
                                                        className={className + '__planEditorFooter__couponRemove'}
                                                        onClick={handleRemoveCoupon}
                                                        disabled={confirmingUpgrade}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className={className + '__planEditorFooter__couponRow'}>
                                                    <input
                                                        className={className + '__planEditorFooter__couponInput'}
                                                        type="text"
                                                        placeholder="Coupon code (optional)"
                                                        value={upgradeCouponInput}
                                                        onChange={e => { setUpgradeCouponInput(e.target.value.toUpperCase()); setUpgradeCouponError(null); }}
                                                        onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                                                        disabled={validatingCoupon || confirmingUpgrade}
                                                    />
                                                    <button
                                                        className={className + '__btn --ghost --sm'}
                                                        onClick={handleApplyCoupon}
                                                        disabled={!upgradeCouponInput.trim() || validatingCoupon || confirmingUpgrade}
                                                    >
                                                        {validatingCoupon ? 'Checking…' : 'Apply'}
                                                    </button>
                                                    {upgradeCouponError && (
                                                        <span className={className + '__planEditorFooter__couponError'}>
                                                            {upgradeCouponError}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {selectedIsDowngrade && (
                                        <span className={className + '__planEditorFooter__downgradeNote'}>
                                            <IoTimeOutline /> Takes effect {formatDate(subscription?.currentPeriodEnd)}
                                        </span>
                                    )}
                                </div>
                                <div className={className + '__planEditorFooter__actions'}>
                                    <button
                                        className={className + '__btn --ghost'}
                                        onClick={() => { setSelectedPlanId(null); setProration(null); setUpgradeCouponInput(''); setUpgradeCouponApplied(null); setUpgradeCouponError(null); }}
                                    >
                                        Cancel
                                    </button>
                                    {selectedIsUpgrade && (
                                        <button
                                            className={className + '__btn --primary'}
                                            onClick={handleUpgrade}
                                            disabled={confirmingUpgrade || loadingProration}
                                        >
                                            {confirmingUpgrade ? 'Processing…' : 'Confirm Upgrade'}
                                        </button>
                                    )}
                                    {selectedIsDowngrade && (
                                        <button
                                            className={className + '__btn --warning'}
                                            onClick={handleScheduleDowngrade}
                                            disabled={confirmingDowngrade}
                                        >
                                            {confirmingDowngrade ? 'Scheduling…' : 'Schedule Downgrade'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </Modal>
        </div>
    );
}
